import { useEffect } from 'react';

declare global {
  interface Window {
    SpotFix?: {
      login: (token: string) => void;
      logout?: () => void;
      open?: () => void;
      close?: () => void;
    };
  }
}

export const SPOTFIX_PROJECT_KEY = "pk_live_U8jMs7E8GhfcApstuyYG36YI";

interface SpotfixWidgetProps {
  userToken?: string | null;
  projectKey?: string;
}

export function openSpotfix(): boolean {
  if (typeof window !== 'undefined' && window.SpotFix?.open) {
    window.SpotFix.open();
    return true;
  }
  return false;
}

export function closeSpotfix(): boolean {
  if (typeof window !== 'undefined' && window.SpotFix?.close) {
    window.SpotFix.close();
    return true;
  }
  return false;
}

export default function SpotfixWidget({
  userToken,
  projectKey = SPOTFIX_PROJECT_KEY,
}: SpotfixWidgetProps) {
  useEffect(() => {
    // 1. If no token, log out if SDK was loaded
    if (!userToken) {
      if (window.SpotFix?.logout) {
        window.SpotFix.logout();
      }
      return;
    }

    const scriptId = 'spotfix-sdk-script';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (existingScript) {
      // Script already injected, login user with token
      if (window.SpotFix?.login) {
        window.SpotFix.login(userToken);
      }
      return;
    }

    // 2. Inject Spotfix SDK
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://api.viewtheme.com/sdk/v1/sdk.js';
    script.setAttribute('data-project', projectKey);
    script.setAttribute('data-user-token', userToken);
    script.async = true;

    script.onload = () => {
      if (window.SpotFix?.login) {
        window.SpotFix.login(userToken);
      }
    };

    script.onerror = (err) => {
      console.warn('[SpotFix] Failed to load SDK script', err);
    };

    document.body.appendChild(script);

    // 3. Cleanup on unmount or user change
    return () => {
      // Do not necessarily destroy script on re-renders, but if userToken becomes null:
      if (!userToken && window.SpotFix?.logout) {
        window.SpotFix.logout();
      }
    };
  }, [userToken, projectKey]);

  // SpotFix renders its own floating widget button
  return null;
}

